import type { Break } from "@payezy/api-client/admin";
import { listBreaksOptions, listReconRunsOptions, resolveBreakMutation, runReconMutation } from "@payezy/api-client/admin/query";
import {
  Badge, Button, Card, CardHeader, Dialog, EmptyState, Field, formatDate, humanize, PageHeader, Select, shortId, Table, Tabs, TD, Textarea, TH, THead, TR, toast,
} from "@payezy/ui";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { RefreshCw } from "lucide-react";
import { useState } from "react";
import { showError } from "../lib/notify";
import { breakKindLabel } from "../lib/staff";

export function ReconPage() {
  const qc = useQueryClient();
  const [status, setStatus] = useState("open");
  const [kind, setKind] = useState("");
  const breaks = useQuery({ ...listBreaksOptions({ query: { status: status === "all" ? undefined : status, kind: kind || undefined, limit: 200 } }), placeholderData: keepPreviousData, refetchInterval: 30_000 });
  const runs = useQuery({ ...listReconRunsOptions({ query: { limit: 15 } }), refetchInterval: 15_000 });
  const run = useMutation({
    ...runReconMutation(),
    onSuccess: () => {
      toast.success("Reconciliation started. Results appear in a minute or two.");
      setTimeout(() => void qc.invalidateQueries(), 5_000);
    },
    onError: showError,
  });
  const [resolving, setResolving] = useState<Break | null>(null);
  const items = breaks.data?.items ?? [];
  return (
    <>
      <PageHeader
        title="Reconciliation"
        description="Hourly comparison of partner statements and balances against the ledger. Breaks close automatically once the next run no longer sees them."
        actions={<Button variant="secondary" loading={run.isPending} onClick={() => run.mutate({})}><RefreshCw className="size-4" /> Run now</Button>}
      />
      <div className="mb-2 flex items-end justify-between gap-3">
        <Tabs value={status} onValueChange={setStatus} tabs={["open", "resolved", "ignored", "all"].map((s) => ({ value: s, label: humanize(s) }))} />
        <Select className="mb-4 w-64" value={kind} onChange={(e) => setKind(e.target.value)}>
          <option value="">All kinds</option>
          {Object.entries(breakKindLabel).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </Select>
      </div>
      <Card>
        <Table>
          <THead><TR><TH>Detected</TH><TH>Kind</TH><TH>Partner</TH><TH>Customer</TH><TH>Reference</TH><TH>Expected</TH><TH>Actual</TH><TH /></TR></THead>
          <tbody>
            {items.map((b) => (
              <TR key={b.id}>
                <TD>{formatDate(b.created_at, true)}</TD>
                <TD>{breakKindLabel[b.kind] ?? b.kind}</TD>
                <TD>{b.partner}</TD>
                <TD>{b.org_id ? <Link to="/customers/$orgId" params={{ orgId: b.org_id }} className="font-mono text-xs hover:underline">{shortId(b.org_id)}</Link> : "—"}</TD>
                <TD className="max-w-48 truncate font-mono text-xs" title={b.reference}>{b.reference}</TD>
                <TD className="text-sm">{b.expected}</TD>
                <TD className="text-sm">{b.actual}</TD>
                <TD className="text-right">
                  {b.status === "open" ? (
                    <Button size="sm" variant="secondary" onClick={() => setResolving(b)}>Resolve</Button>
                  ) : (
                    <span className="text-xs text-ink-500" title={b.resolution_note}>{humanize(b.status)}</span>
                  )}
                </TD>
              </TR>
            ))}
          </tbody>
        </Table>
        {!breaks.isLoading && items.length === 0 && <EmptyState title={status === "open" ? "No open breaks" : "Nothing here"} />}
      </Card>
      <Card className="mt-6">
        <CardHeader title="Recent runs" />
        <Table>
          <THead><TR><TH>Started</TH><TH>Kind</TH><TH>Status</TH><TH>Stats</TH></TR></THead>
          <tbody>
            {runs.data?.items.map((r) => (
              <TR key={r.id}>
                <TD>{formatDate(r.started_at, true)}</TD>
                <TD>{r.kind}</TD>
                <TD><Badge tone={r.status === "completed" ? "success" : r.status === "failed" ? "danger" : "info"}>{r.status}</Badge></TD>
                <TD className="text-xs text-ink-600">
                  {Object.entries(r.stats).filter(([, v]) => Number(v) > 0).map(([k, v]) => `${humanize(k)} ${String(v)}`).join(" · ") || "—"}
                </TD>
              </TR>
            ))}
          </tbody>
        </Table>
      </Card>
      {resolving && <ResolveDialog b={resolving} onClose={() => setResolving(null)} />}
    </>
  );
}

function ResolveDialog({ b, onClose }: { b: Break; onClose: () => void }) {
  const qc = useQueryClient();
  const [status, setStatus] = useState<"resolved" | "ignored">("resolved");
  const [note, setNote] = useState("");
  const resolve = useMutation({
    ...resolveBreakMutation(),
    onSuccess: () => {
      onClose();
      void qc.invalidateQueries();
    },
    onError: showError,
  });
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={breakKindLabel[b.kind] ?? b.kind}
      description={`${b.partner} · ${b.reference}`}
      wide
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button loading={resolve.isPending} disabled={note.trim().length < 5} onClick={() => resolve.mutate({ path: { breakId: b.id }, body: { status, note } })}>Save</Button>
        </>
      }
    >
      <pre className="mb-4 max-h-56 overflow-auto rounded-lg bg-ink-50 p-3 text-xs">{JSON.stringify(b.details, null, 2)}</pre>
      <div className="flex flex-col gap-4">
        <Field label="Outcome" hint="Ignored breaks with the same reference won't reopen.">
          <Select value={status} onChange={(e) => setStatus(e.target.value as "resolved" | "ignored")}>
            <option value="resolved">Resolved — the underlying issue was fixed</option>
            <option value="ignored">Ignored — expected difference</option>
          </Select>
        </Field>
        <Field label="Note"><Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Partner confirmed the transfer was reversed (case 88123)." /></Field>
      </div>
    </Dialog>
  );
}
